'use client'

import { ChevronDown, List } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { TocEntry } from '@/utilities/extractTocFromLexical'
import { buildTocTree } from '@/utilities/extractTocFromLexical'
import { cn } from '@/utilities/ui'

import {
  scrollToHeading,
  TOC_SCROLLBAR_CLASSES,
  TocNestedList,
  TocSemrushHeading,
  useActiveTocId,
} from './tableOfContentsShared'

export type TableOfContentsVariant = 'inline-mobile' | 'sidebar-desktop'

type Props = {
  entries: readonly TocEntry[]
  variant: TableOfContentsVariant
  heading?: string
  className?: string
  enableScrollSpy?: boolean
}

// ── Mobile inline: pill button sticky + dropdown ───────────────────────────

type InlineMobileProps = {
  entries: readonly TocEntry[]
  heading: string
  className?: string
  enableScrollSpy: boolean
}

function InlineMobileToc({ entries, heading, className, enableScrollSpy }: InlineMobileProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const activeId = useActiveTocId(entries, enableScrollSpy)
  const tree = useMemo(() => buildTocTree(entries), [entries])

  const handleActivate = useCallback((id: string) => {
    scrollToHeading(id)
    setOpen(false)
  }, [])

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (!entries.length) return null

  return (
    <div
      ref={containerRef}
      className={cn('not-prose lg:hidden sticky top-24 z-20 -mx-4 px-4 mb-4', className)}
      data-toc-variant="inline-mobile"
    >
      {/* Overlay leve atrás do dropdown */}
      <div
        aria-hidden
        onClick={() => setOpen(false)}
        className={cn(
          'fixed inset-0 z-[19] bg-black/25 transition-opacity duration-200',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      />

      {/* Pill button — estilo Semrush */}
      <button
        type="button"
        aria-expanded={open}
        aria-label={heading}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'relative z-[21] flex w-full items-center gap-2.5 rounded-full border border-border bg-background px-4 py-2.5',
          'text-sm font-semibold text-foreground shadow-sm',
          'transition-colors hover:bg-muted/40',
        )}
      >
        <List className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="flex-1 text-left">{heading}</span>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>

      {/* Dropdown com a lista — abre/fecha com animação CSS grid */}
      <div
        aria-hidden={!open}
        className={cn(
          'absolute left-4 right-4 z-[21] mt-1.5 overflow-hidden rounded-xl border border-border bg-background shadow-lg',
          'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0 pointer-events-none',
        )}
      >
        <div className="min-h-0">
          <div className={cn('max-h-[min(60vh,24rem)] px-2 py-3', TOC_SCROLLBAR_CLASSES)}>
            <TocNestedList
              nodes={tree}
              activeId={activeId}
              depth={0}
              onActivate={handleActivate}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Componente principal ───────────────────────────────────────────────────

export function TableOfContents({
  entries,
  variant,
  heading: headingProp,
  className,
  enableScrollSpy,
}: Props) {
  const t = useTranslations('Blog')
  const heading = headingProp ?? t('tocHeading')
  const spy = enableScrollSpy ?? true
  const activeId = useActiveTocId(entries, spy && variant === 'sidebar-desktop')
  const tree = useMemo(() => buildTocTree(entries), [entries])
  const onActivate = useCallback((id: string) => scrollToHeading(id), [])

  if (!entries.length) return null

  if (variant === 'inline-mobile') {
    return (
      <InlineMobileToc
        entries={entries}
        heading={heading}
        className={className}
        enableScrollSpy={spy}
      />
    )
  }

  // sidebar-desktop
  return (
    <div className={cn('not-prose hidden lg:block', className)} data-toc-variant="sidebar-desktop">
      <nav
        id="article-toc-sidebar"
        data-test="toc-desktop"
        aria-label={t('tableOfContents')}
        className="flex flex-col gap-3"
      >
        <TocSemrushHeading className="pl-3">{heading}</TocSemrushHeading>
        <TocNestedList
          nodes={tree}
          activeId={activeId}
          depth={0}
          onActivate={onActivate}
        />
      </nav>
    </div>
  )
}
